import httpClient from "../http/http-client";
import { ISecrEnergySourceLine } from "../generated-report/generated-report.model";
import { IHotspotAnalysis, IHotspotDetail } from "./hotspots.model";
import { IBreakdownAnalysis } from "./breakdown.model";

/** One mappable location: emissions value plus the coordinates resolved from its address. */
export interface IMapDataPoint {
  id: string;
  label: string;
  postcode: string | null;
  country: string | null;
  lat: number;
  lng: number;
  /** 'postcode' = UK postcode-district centroid; 'country' = country centroid (approximate). */
  precision: 'postcode' | 'country';
  value: number;
}

/**
 * A location that could not be placed on the map. Carries its current address fields so the
 * "update information" dialog can prefill rather than blank them.
 */
export interface IUnmappedLocation {
  id: string;
  label: string;
  postcode: string | null;
  countryId: string | null;
}

export interface IMapDataResponse {
  locationBased: IMapDataPoint[];
  marketBased: IMapDataPoint[];
  /** Locations without resolvable coordinates are excluded from the arrays and listed here. */
  coverage: {
    total: number;
    mapped: number;
    unmapped: number;
    unmappedLocations: IUnmappedLocation[];
  };
}

export interface IIntensityRatioResult {
  intensityRatio: string;
  label?: string;
  unitLabel?: string;
  denominatorType?: 'revenue' | 'employees' | 'floorArea';
  includesScopeThree?: boolean;
  denominatorAvailable?: boolean;
  emissions: { uk: number | null; overseas: number | null; global: number | null };
}

export default class AnalyticsService {
  http;

  constructor() {
    this.http = httpClient
  }

  async getStackedData(reportPeriodId: string, queryString: string): Promise<any> {
    return this.http.get(`analytics/stacked/${reportPeriodId}${queryString ? queryString : ''}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async getBarData(reportPeriodId: string, queryString: string): Promise<any> {
    return this.http.get(`analytics/bar/${reportPeriodId}${queryString ? queryString : ''}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async getPieData(reportPeriodId: string, queryString: string): Promise<any> {
    return this.http.get(`analytics/pie/${reportPeriodId}${queryString ? queryString : ''}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  /**
   * Geographic breakdown for the emissions map. Values are the same tCO2e figures the pie/bar
   * charts use; coordinates come from the location record (resolved offline on write).
   */
  async getMapData(reportPeriodId: string, queryString: string): Promise<IMapDataResponse> {
    return this.http.get(`analytics/map/${reportPeriodId}${queryString ? queryString : ''}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async getBreakdownTotal(
    reportPeriodId: string,
    scopeTwoMethod?: 'market' | 'location',
    includeFlagBreakdown?: boolean,
    locationId?: string,
  ): Promise<any> {
    const params = new URLSearchParams();
    if (scopeTwoMethod) params.set('scopeTwoMethod', scopeTwoMethod);
    if (includeFlagBreakdown) params.set('includeFlagBreakdown', 'true');
    if (locationId) params.set('locationId', locationId);
    const qs = params.toString();
    const query = qs ? `?${qs}` : '';
    return this.http.get(`analytics/total/${reportPeriodId}${query}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async getDataQualityMix(reportPeriodId: string): Promise<import('../generated-report/generated-report.model').IDataQualityMix> {
    return this.http.get(`analytics/data-quality/${reportPeriodId}`)
      .then(response => response.data)
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async getIntensityRatios(reportPeriodId: string): Promise<IIntensityRatioResult[]> {
    return this.http.get(`analytics/intensity-ratios/${reportPeriodId}`)
      .then(response => response.data)
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async getEnergyBreakdownTotal(reportPeriodId: string): Promise<any> {
    return this.http.get(`analytics/energy-total/${reportPeriodId}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async getEnergySourceBreakdown(reportPeriodId: string): Promise<ISecrEnergySourceLine[]> {
    return this.http.get(`analytics/energy-source-breakdown/${reportPeriodId}`)
      .then(response => response.data)
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  /**
   * Emissions Hotspot Analysis payload: ranked concentration (Pareto), period-over-period
   * trend, prioritisation matrix, data-quality signal and reduction context.
   */
  async getHotspots(
    reportPeriodId: string,
    opts?: { scopeTwoMethod?: 'location' | 'market'; comparisonReportPeriodId?: string; limit?: number },
  ): Promise<IHotspotAnalysis> {
    const params = new URLSearchParams();
    if (opts?.scopeTwoMethod) params.set('scopeTwoMethod', opts.scopeTwoMethod);
    if (opts?.comparisonReportPeriodId) params.set('comparisonReportPeriodId', opts.comparisonReportPeriodId);
    if (opts?.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return this.http.get(`analytics/hotspots/${reportPeriodId}${qs ? `?${qs}` : ''}`)
      .then(response => response.data)
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  /**
   * Emissions Breakdown (composition) payload: every entity at one level, the totals for the
   * entities the user picked, an "everything else" bucket, the reconciliation figures and an
   * optional monthly trend.
   *
   * One request serves both the picker and the charts, because the response always carries the full
   * untruncated entity list. So changing the selection is one call, not a call plus a list refresh.
   */
  async getComposition(
    reportPeriodId: string,
    opts?: {
      level?: string;
      ids?: string[];
      includeOther?: boolean;
      timeSeries?: boolean;
      scopeTwoMethod?: 'location' | 'market';
    },
  ): Promise<IBreakdownAnalysis> {
    const params = new URLSearchParams();
    if (opts?.level) params.set('level', opts.level);
    if (opts?.ids?.length) params.set('ids', opts.ids.join(','));
    if (opts?.includeOther) params.set('includeOther', 'true');
    if (opts?.timeSeries) params.set('timeSeries', 'true');
    if (opts?.scopeTwoMethod) params.set('scopeTwoMethod', opts.scopeTwoMethod);
    const qs = params.toString();
    return this.http.get(`analytics/composition/${reportPeriodId}${qs ? `?${qs}` : ''}`)
      .then(response => response.data)
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  /**
   * Drill-down detail for one hotspot entity: this-year-vs-last-year by month,
   * a "what's inside" sub-breakdown, and a records-filter for deep-linking.
   */
  async getHotspotDetail(
    reportPeriodId: string,
    opts: { dimension: string; id: string; label?: string; scopeTwoMethod?: 'location' | 'market'; comparisonReportPeriodId?: string },
  ): Promise<IHotspotDetail> {
    const params = new URLSearchParams();
    params.set('dimension', opts.dimension);
    params.set('id', opts.id);
    if (opts.label) params.set('label', opts.label);
    if (opts.scopeTwoMethod) params.set('scopeTwoMethod', opts.scopeTwoMethod);
    if (opts.comparisonReportPeriodId) params.set('comparisonReportPeriodId', opts.comparisonReportPeriodId);
    return this.http.get(`analytics/hotspots/${reportPeriodId}/detail?${params.toString()}`)
      .then(response => response.data)
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  /**
   * The Hotspot Analysis view as an A4 landscape PDF. `compare` is the resolved comparison period
   * id (omitted when there is none), so the PDF compares against what the screen shows; `rows` is
   * 'all' when the ranked table is expanded.
   */
  async getHotspotsPdf(
    reportPeriodId: string,
    opts: { dim: string; basis: 'location' | 'market'; compare?: string; rows: 'all' | 'top' },
  ): Promise<Blob> {
    const params = new URLSearchParams();
    params.set('dim', opts.dim);
    params.set('basis', opts.basis);
    if (opts.compare) params.set('compare', opts.compare);
    params.set('rows', opts.rows);
    return this.http.get(`analytics/hotspots/${reportPeriodId}/pdf?${params.toString()}`, { responseType: 'blob' })
      .then(response => response.data)
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  /**
   * FLAG analytics dashboard payload (403 when organisation FLAG feature is off).
   */
  async getFlagDashboard(
    reportPeriodId: string,
    scopeTwoMethod: 'market' | 'location',
    extraQuery?: string,
  ): Promise<any> {
    const params = new URLSearchParams();
    params.set('scopeTwoMethod', scopeTwoMethod);
    if (extraQuery) {
      const normalized = extraQuery.startsWith('?') ? extraQuery.slice(1) : extraQuery;
      const more = new URLSearchParams(normalized);
      more.forEach((v, k) => {
        params.set(k, v);
      });
    }
    return this.http.get(`analytics/flag-dashboard/${reportPeriodId}?${params.toString()}`)
      .then(response => response.data)
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  /**
   * Records that a built-in analytics dashboard tab was viewed. Fire-and-forget: the caller
   * should not await it or surface failures, since it is telemetry, not part of the page load.
   */
  async trackDashboardView(tab: string): Promise<void> {
    return this.http.post('analytics/dashboard-view', { tab })
      .then(() => undefined)
      .catch(() => undefined);
  }
}